import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import Layout from '../../components/Layout/Layout';
import {
  Card, Button, Badge, SearchInput, Select, Spinner, EmptyState,
  Table, TableHead, TableBody, TableRow, TableCell, Modal, Tabs,
  ImagePreviewModal, isImageAttachment, Tooltip,
  stickyActionCellClass, stickyActionHeaderClass
} from '../../components/UI';
import { formatLabel } from '../../utils/formatLabel';
import api, { callLogsAPI, whatsappTemplatesAPI, filePublicUrl } from '../../services/api';
import toast from 'react-hot-toast';
import {
  HiOutlinePhone, HiOutlinePlay, HiOutlineDownload, HiOutlineUser,
  HiOutlineClipboardList, HiOutlinePaperClip,
  HiOutlineChevronUp, HiOutlineChevronDown
} from 'react-icons/hi';
import { FaWhatsapp } from 'react-icons/fa';
import { format } from 'date-fns';
import TalkTimeAudio from '../Recordings/TalkTimeAudio';

// Build a compact list of page numbers + "..." ellipses for pagination.
// Always shows first and last page, current page ±1, with ellipses for gaps.
// e.g. page 5 of 20 -> [1, "...", 4, 5, 6, "...", 20]
const buildPageList = (current, total) => {
  const pages = [];
  const set = new Set([1, total, current, current - 1, current + 1]);
  for (const p of set) {
    if (p >= 1 && p <= total) pages.push(p);
  }
  pages.sort((a, b) => a - b);

  const result = [];
  for (let i = 0; i < pages.length; i++) {
    result.push(pages[i]);
    if (i < pages.length - 1 && pages[i + 1] - pages[i] > 1) {
      result.push('...');
    }
  }
  return result;
};

// Format duration the same way the Recordings section does (m:ss, "0:00" when empty)
const formatDuration = (seconds) => {
  if (!seconds) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// Resolve the recording ID for playback. The backend may populate either
// recordingPublicId (the Recording doc _id) or recordingUrl (/api/recordings/file/<id>).
// We prefer recordingPublicId because it's the authoritative link, but fall back
// to extracting the ID from recordingUrl when necessary.
// (Module-level pure function so the audio-loading effect can list it safely.)
const getRecordingId = (log) => {
  if (!log) return null;
  if (log.recordingPublicId) return log.recordingPublicId;
  if (log.recordingUrl) {
    // Extract ID from paths like "/api/recordings/file/67a1b2c3d4e5f6..."
    const match = log.recordingUrl.match(/\/recordings\/file\/([a-f\d]{24})/i);
    if (match) return match[1];
  }
  return null;
};

const CallLogs = () => {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [phoneFilter, setPhoneFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState(''); // 'asc' | 'desc'
  const [selectedLog, setSelectedLog] = useState(null);
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'app' or 'device'
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [selectedLogForWhatsApp, setSelectedLogForWhatsApp] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const audioUrlRef = useRef(null);

  // Fetch audio URL with authentication when a log is selected for playback
  useEffect(() => {
    const recordingId = selectedLog ? getRecordingId(selectedLog) : null;
    if (recordingId) {
      const fetchAudio = async () => {
        try {
          // Revoke previous blob URL if any
          if (audioUrlRef.current) {
            URL.revokeObjectURL(audioUrlRef.current);
            audioUrlRef.current = null;
          }
          const response = await api.get(`/recordings/file/${recordingId}`, {
            responseType: 'blob'
          });
          const url = URL.createObjectURL(response.data);
          audioUrlRef.current = url;
          setAudioUrl(url);
        } catch (error) {
          console.error('Failed to load recording:', error);
          toast.error('Failed to load recording');
          setAudioUrl(null);
        }
      };
      fetchAudio();
    } else {
      // Modal closed or no recording — revoke blob URL
      if (audioUrlRef.current) {
        URL.revokeObjectURL(audioUrlRef.current);
        audioUrlRef.current = null;
      }
      setAudioUrl(null);
    }
    return () => {
      if (audioUrlRef.current) {
        URL.revokeObjectURL(audioUrlRef.current);
        audioUrlRef.current = null;
      }
    };
  }, [selectedLog]);

  // Fetch users with phones for filter
  const { data: usersWithPhones } = useQuery({
    queryKey: ['usersWithPhones'],
    queryFn: async () => {
      const response = await callLogsAPI.getUsersWithPhones();
      return response.data?.data || { users: [], phoneNumbers: [] };
    },
  });

  // Fetch call logs with filters
  const { data: logsData, isLoading } = useQuery({
    queryKey: ['callLogs', page, pageSize, statusFilter, userFilter, phoneFilter, search, activeTab, sortBy, sortOrder],
    queryFn: async () => {
      const params = { page, limit: pageSize };
      if (statusFilter) params.status = statusFilter;
      if (userFilter) params.userId = userFilter;
      if (phoneFilter) params.phoneNumber = phoneFilter;
      if (search) params.search = search;
      if (activeTab && activeTab !== 'all') params.source = activeTab;
      if (sortBy) params.sortBy = sortBy;
      if (sortOrder) params.sortOrder = sortOrder;
      const response = await callLogsAPI.getAll(params);
      return response.data || {};
    },
  });

  // Fetch statistics (all sources, all time — used for both stats cards and tab counts)
  const { data: stats } = useQuery({
    queryKey: ['callStats'],
    queryFn: async () => {
      const response = await callLogsAPI.getStatistics({ source: 'all', range: 'all' });
      return response.data?.data || {};
    },
  });

  // sourceStats is the same data — avoid duplicate request
  const sourceStats = stats;

  // Fetch WhatsApp templates
  const { data: templatesData } = useQuery({
    queryKey: ['whatsappTemplates'],
    queryFn: async () => {
      const response = await whatsappTemplatesAPI.getActive();
      return response.data?.data || [];
    },
  });

  const logs = logsData?.data || [];
  const pagination = logsData?.pagination || {};
  const users = usersWithPhones?.users || [];
  const templates = templatesData || [];

  // Get phones for selected user
  const selectedUser = users.find(u => u._id === userFilter);
  const userPhones = selectedUser?.phones?.map(p => ({
    value: p.number,
    label: `${p.number} (${p.label || 'Phone'})`
  })) || [];

  const totalCalls = stats?.total || 0;
  const connectedCalls = stats?.connected || 0;
  const avgDuration = stats?.averageDuration || 0;

  // Get counts by source
  const appCallsCount = sourceStats?.bySource?.app?.total || 0;
  const deviceCallsCount = sourceStats?.bySource?.device?.total || 0;
  const allCallsCount = sourceStats?.total || 0;

  // Tabs for call source
  const tabs = [
    { value: 'all', label: 'All Calls', count: allCallsCount },
    { value: 'app', label: 'App Calls', count: appCallsCount },
    { value: 'device', label: 'Device Calls', count: deviceCallsCount },
  ];

  // Show logs filtered by source
  const filteredLogs = logs;

  // Sort handler — cycles: asc → desc → clear
  const handleSort = (field) => {
    if (sortBy === field) {
      if (sortOrder === 'asc') {
        setSortOrder('desc');
      } else {
        // desc → clear (no sort)
        setSortBy('');
        setSortOrder('');
      }
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setPage(1);
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) {
      return (
        <span className="inline-flex flex-col ml-0.5 opacity-30">
          <HiOutlineChevronUp className="w-3 h-3 -mb-1" />
          <HiOutlineChevronDown className="w-3 h-3" />
        </span>
      );
    }
    return sortOrder === 'asc' ? (
      <HiOutlineChevronUp className="w-3.5 h-3.5 ml-0.5 text-primary-600" />
    ) : (
      <HiOutlineChevronDown className="w-3.5 h-3.5 ml-0.5 text-primary-600" />
    );
  };

  // Open WhatsApp modal
  const handleOpenWhatsApp = (log) => {
    setSelectedLogForWhatsApp(log);
    setSelectedTemplate(null);
    setShowWhatsAppModal(true);
  };

  // Direct WhatsApp redirect without template
  const handleDirectWhatsApp = (log) => {
    const phoneNumber = log.phoneNumber?.replace(/\D/g, '') || '';
    if (!phoneNumber) {
      toast.error('Invalid phone number');
      return;
    }
    const whatsappUrl = `https://wa.me/${phoneNumber}`;
    window.open(whatsappUrl, '_blank');
    toast.success('Opening WhatsApp...');
  };

  // Download a call's recording file (used from the Actions column and the modal)
  const handleDownloadRecording = async (log) => {
    try {
      const recId = getRecordingId(log);
      if (!recId) {
        toast.error('No recording available');
        return;
      }
      const response = await api.get(`/recordings/file/${recId}`, {
        responseType: 'blob'
      });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `recording-${log.phoneNumber}-${log._id}.mp3`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success('Download started');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download recording');
    }
  };

  // Generate WhatsApp link with template and open
  const handleSendWhatsApp = () => {
    if (!selectedLogForWhatsApp || !selectedTemplate) {
      toast.error('Please select a template');
      return;
    }

    const phoneNumber = selectedLogForWhatsApp.phoneNumber?.replace(/\D/g, '') || '';
    if (!phoneNumber) {
      toast.error('Invalid phone number');
      return;
    }

    // Replace template variables with lead data
    let message = selectedTemplate.message;
    const leadName = selectedLogForWhatsApp.leadId?.name || selectedLogForWhatsApp.contactName || 'Customer';
    const leadEmail = selectedLogForWhatsApp.leadId?.email || '';

    message = message.replace(/\{\{name\}\}/gi, leadName);
    message = message.replace(/\{\{email\}\}/gi, leadEmail);
    message = message.replace(/\{\{phone\}\}/gi, selectedLogForWhatsApp.phoneNumber || '');

    // Create WhatsApp URL
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;

    // Open in new tab
    window.open(whatsappUrl, '_blank');
    setShowWhatsAppModal(false);
    toast.success('Redirecting to WhatsApp...');
  };

  // Export call logs to CSV
  const handleExportCallLogs = () => {
    try {
      if (filteredLogs.length === 0) {
        toast.error('No call logs to export');
        return;
      }

      // Create CSV rows
      const csvRows = [];

      // Header section
      csvRows.push('=== CALL LOGS EXPORT ===');
      csvRows.push('');
      csvRows.push(`Export Date: ${new Date().toLocaleString()}`);
      csvRows.push(`Total Calls: ${totalCalls}`);
      csvRows.push(`Connected Calls: ${connectedCalls}`);
      csvRows.push(`Not Connected: ${totalCalls - connectedCalls}`);
      csvRows.push(`Average Duration: ${Math.floor(avgDuration / 60)}m ${avgDuration % 60}s`);
      csvRows.push('');

      // Column headers - comprehensive
      csvRows.push([
        'Phone Number',
        'Lead Name',
        'Lead Email',
        'Call Type',
        'Status',
        'Duration (seconds)',
        'Duration (formatted)',
        'Has Recording',
        'Notes',
        'Date',
        'Time'
      ].join(','));

      // Data rows
      filteredLogs.forEach(log => {
        const durationMin = Math.floor((log.duration || 0) / 60);
        const durationSec = (log.duration || 0) % 60;

        csvRows.push([
          `"${log.phoneNumber || ''}"`,
          `"${log.leadId?.name || 'Unknown'}"`,
          `"${log.leadId?.email || ''}"`,
          `"${log.callType || 'outgoing'}"`,
          `"${log.status || ''}"`,
          `"${log.duration || 0}"`,
          `"${durationMin}m ${durationSec}s"`,
          `"${log.recordingUrl ? 'Yes' : 'No'}"`,
          `"${[log.notes, log.remarks].filter(Boolean).join(' — ').replace(/"/g, '""')}"`,
          `"${log.createdAt ? format(new Date(log.createdAt), 'yyyy-MM-dd') : ''}"`,
          `"${log.createdAt ? format(new Date(log.createdAt), 'HH:mm:ss') : ''}"`
        ].join(','));
      });

      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `call-logs-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      toast.success(`Exported ${filteredLogs.length} call logs successfully`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export call logs');
    }
  };

  return (
    <Layout>
      <div className="p-4 lg:p-6 space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
          <div className="min-w-0">
            <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">Call Logs</h1>
            <p className="text-gray-500 text-xs sm:text-sm mt-0.5 sm:mt-1">View all call recordings and history</p>
          </div>
          <Tooltip text="Export call logs to CSV">
            <Button
              variant="outline"
              size="sm"
              icon={<HiOutlineDownload className="w-4 h-4" />}
              onClick={handleExportCallLogs}
            >
              <span className="hidden sm:inline">Export</span>
            </Button>
          </Tooltip>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 lg:gap-4">
          <Card padding="p-2.5 sm:p-3 lg:p-4" className="text-center">
            <p className="text-xs text-gray-500">Total Calls</p>
            <p className="text-base sm:text-lg lg:text-2xl font-bold text-gray-900">{totalCalls}</p>
          </Card>
          <Card padding="p-2.5 sm:p-3 lg:p-4" className="text-center bg-green-50">
            <p className="text-xs text-green-600">Connected</p>
            <p className="text-base sm:text-lg lg:text-2xl font-bold text-green-600">{connectedCalls}</p>
          </Card>
          <Card padding="p-2.5 sm:p-3 lg:p-4" className="text-center bg-blue-50">
            <p className="text-xs text-blue-600">Avg Duration</p>
            <p className="text-base sm:text-lg lg:text-2xl font-bold text-blue-600">
              {Math.floor(avgDuration / 60)}:{(avgDuration % 60).toString().padStart(2, '0')}
            </p>
          </Card>
          <Card padding="p-2.5 sm:p-3 lg:p-4" className="text-center bg-purple-50">
            <p className="text-xs text-purple-600">Rate</p>
            <p className="text-base sm:text-lg lg:text-2xl font-bold text-purple-600">
              {totalCalls > 0 ? Math.round((connectedCalls / totalCalls) * 100) : 0}%
            </p>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs tabs={tabs} activeTab={activeTab} onChange={(tab) => { setActiveTab(tab); setPage(1); }} />

        {/* Filters */}
        <Card padding="p-3 sm:p-4">
          <div className="flex flex-col gap-3">
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <div className="flex-1">
                <SearchInput
                  value={search}
                  onChange={(val) => { setSearch(val); setPage(1); }}
                  placeholder="Search by phone or lead..."
                />
              </div>
              <div className="flex gap-2">
                <Select
                  options={[
                    { value: '', label: 'All Status' },
                    { value: 'connected', label: 'Connected' },
                    { value: 'not_connected', label: 'Not Connected' },
                    { value: 'busy', label: 'Busy' },
                    { value: 'no_answer', label: 'No Answer' },
                  ]}
                  value={statusFilter}
                  onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                  className="flex-1 min-w-[100px]"
                />
                <Select
                  options={[
                    { value: '', label: 'All Users' },
                    ...users.map(u => ({
                      value: u._id,
                      label: u.name
                    }))
                  ]}
                  value={userFilter}
                  onChange={(e) => {
                    setUserFilter(e.target.value);
                    setPhoneFilter(''); // Reset phone filter when user changes
                    setPage(1);
                  }}
                  className="flex-1 min-w-[100px]"
                />
                <div className="flex items-center gap-1.5">
                  <span className="text-xs sm:text-sm text-gray-500 whitespace-nowrap">Rows per page</span>
                  <Select
                    options={[
                      { value: 25, label: '25' },
                      { value: 50, label: '50' },
                      { value: 100, label: '100' },
                      { value: 200, label: '200' },
                    ]}
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setPage(1);
                    }}
                    className="flex-1 min-w-[70px]"
                  />
                </div>
              </div>
            </div>
            {userFilter && userPhones.length > 0 && (
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                <Select
                  options={[
                    { value: '', label: 'All Phones' },
                    ...userPhones
                  ]}
                  value={phoneFilter}
                  onChange={(e) => { setPhoneFilter(e.target.value); setPage(1); }}
                  className="sm:w-48"
                />
              </div>
            )}
          </div>
        </Card>

        {/* Call Logs List */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : filteredLogs.length === 0 ? (
          <Card>
            <EmptyState
              icon={<HiOutlinePhone className="w-8 h-8 text-gray-400" />}
              title="No call logs found"
              description="Call logs will appear here once calls are made"
            />
          </Card>
        ) : (
          <Card padding="p-0">
            {/* Desktop Table */}
            <div className="hidden md:block">
              <Table>
                <TableHead>
                  <TableRow hoverable={false}>
                    <TableCell header className="min-w-[100px] cursor-pointer select-none hover:text-primary-700 transition-colors whitespace-nowrap" onClick={() => handleSort('phoneNumber')}>
                      <span className="inline-flex items-center">Phone <SortIcon field="phoneNumber" /></span>
                    </TableCell>
                    <TableCell header className="min-w-[120px]">Contact/Lead</TableCell>
                    <TableCell header className="min-w-[100px]">User</TableCell>
                    <TableCell header className="min-w-[80px] cursor-pointer select-none hover:text-primary-700 transition-colors whitespace-nowrap" onClick={() => handleSort('callType')}>
                      <span className="inline-flex items-center">Type <SortIcon field="callType" /></span>
                    </TableCell>
                    <TableCell header className="min-w-[90px] cursor-pointer select-none hover:text-primary-700 transition-colors whitespace-nowrap" onClick={() => handleSort('status')}>
                      <span className="inline-flex items-center">Status <SortIcon field="status" /></span>
                    </TableCell>
                    <TableCell header className="min-w-[60px] cursor-pointer select-none hover:text-primary-700 transition-colors whitespace-nowrap" onClick={() => handleSort('duration')}>
                      <span className="inline-flex items-center">Duration <SortIcon field="duration" /></span>
                    </TableCell>
                    <TableCell header className="min-w-[100px] cursor-pointer select-none hover:text-primary-700 transition-colors whitespace-nowrap" onClick={() => handleSort('createdAt')}>
                      <span className="inline-flex items-center">Date <SortIcon field="createdAt" /></span>
                    </TableCell>
                    <TableCell header className={`min-w-[120px] ${stickyActionHeaderClass}`}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredLogs.map((log, index) => {
                    // The first row has no room above it for a tooltip to pop
                    // up into before it gets clipped by this table's scroll
                    // container — point its tooltips down instead.
                    const tooltipPos = index === 0 ? 'bottom' : 'top'
                    return (
                    <TableRow key={log._id}>
                      <TableCell>
                        <span className="font-medium text-gray-900 whitespace-nowrap">{log.phoneNumber}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-gray-600 truncate max-w-[150px] block">
                          {log.leadId?.name || log.contactName || '-'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Tooltip position={tooltipPos} text={`${log.userId?.firstName || ''} ${log.userId?.lastName || ''}`.trim() || 'Unknown'}>
                          <div className="flex items-center gap-2">
                            <HiOutlineUser className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            <span className="text-gray-600 text-sm truncate max-w-[100px]">
                              {log.userId?.firstName} {log.userId?.lastName}
                            </span>
                          </div>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            log.callType === 'incoming' ? 'info' :
                              log.callType === 'missed' ? 'danger' : 'primary'
                          }
                          size="sm"
                        >
                          {formatLabel(log.callType || 'outgoing')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={log.status === 'connected' ? 'success' : 'danger'}
                          size="sm"
                        >
                          {formatLabel(log.status)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-gray-600 whitespace-nowrap">
                          {formatDuration(log.duration)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-gray-500 text-sm whitespace-nowrap">
                          {log.createdAt ? format(new Date(log.createdAt), 'MMM d, h:mm a') : '-'}
                        </span>
                      </TableCell>
                      <TableCell className={stickyActionCellClass}>
                        <div className="flex items-center gap-1 flex-nowrap">
                          {/* Recording Play Button — active when a linked recording exists */}
                          {(log.recordingPublicId || log.recordingUrl) ? (
                            <Tooltip position={tooltipPos} text="Play Recording">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="p-1.5"
                                icon={<HiOutlinePlay className="w-4 h-4 text-primary-600" />}
                                onClick={() => setSelectedLog(log)}
                              />
                            </Tooltip>
                          ) : (
                            <span className="w-8 h-8 flex items-center justify-center text-gray-300">
                              <HiOutlinePlay className="w-4 h-4" />
                            </span>
                          )}
                          {/* Recording Download Button in Actions */}
                          {(log.recordingPublicId || log.recordingUrl) && (
                            <Tooltip position={tooltipPos} text="Download Recording">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="p-1.5"
                                icon={<HiOutlineDownload className="w-4 h-4 text-blue-600" />}
                                onClick={() => handleDownloadRecording(log)}
                              />
                            </Tooltip>
                          )}
                          {/* WhatsApp Direct Button */}
                          <Tooltip position={tooltipPos} text="Open WhatsApp Chat">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="p-1.5"
                              icon={<FaWhatsapp className="w-4 h-4 text-green-600" />}
                              onClick={() => handleDirectWhatsApp(log)}
                            />
                          </Tooltip>
                          {/* WhatsApp with Template Button */}
                          <Tooltip position={tooltipPos} text="Send via Template">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="p-1.5"
                              icon={<HiOutlineClipboardList className="w-4 h-4 text-green-700" />}
                              onClick={() => handleOpenWhatsApp(log)}
                            />
                          </Tooltip>
                        </div>
                      </TableCell>
                    </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden divide-y divide-gray-100">
              {filteredLogs.map((log, index) => (
                <div key={log._id} className="p-3 sm:p-4 hover:bg-gray-50 transition-colors">
                  {/* Header Row */}
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-gray-900 truncate">{log.phoneNumber}</p>
                      <p className="text-xs sm:text-sm text-gray-500 truncate">
                        {log.leadId?.name || log.contactName || 'Unknown Lead'}
                      </p>
                      <div className="text-xs text-gray-400 mt-0.5">
                        <HiOutlineUser className="w-3 h-3 inline mr-1" />
                        <Tooltip position={index === 0 ? 'bottom' : 'top'} text={`${log.userId?.firstName || ''} ${log.userId?.lastName || ''}`.trim() || 'Unknown'}>
                          <span>{log.userId?.firstName} {log.userId?.lastName}</span>
                        </Tooltip>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      <Badge
                        variant={
                          log.callType === 'incoming' ? 'info' :
                            log.callType === 'missed' ? 'danger' : 'primary'
                        }
                        size="sm"
                      >
                        {formatLabel(log.callType || 'outgoing')}
                      </Badge>
                      <Badge
                        variant={log.status === 'connected' ? 'success' : 'danger'}
                        size="sm"
                      >
                        {formatLabel(log.status)}
                      </Badge>
                    </div>
                  </div>

                  {/* Info Row */}
                  <div className="flex items-center justify-between text-xs sm:text-sm mb-3">
                    <span className="text-gray-400">
                      {log.createdAt ? format(new Date(log.createdAt), 'MMM d, h:mm a') : '-'}
                    </span>
                    <span className="text-gray-500 font-medium">
                      {formatDuration(log.duration)}
                    </span>
                  </div>

                  {/* Actions Row */}
                  <div className="flex items-center gap-1.5 pt-2 border-t border-gray-100">
                    {(log.recordingPublicId || log.recordingUrl) ? (
                      <Tooltip position={index === 0 ? 'bottom' : 'top'} text="Play Recording">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="p-2 flex-1"
                          icon={<HiOutlinePlay className="w-4 h-4 text-primary-600" />}
                          onClick={() => setSelectedLog(log)}
                        >
                          <span className="text-xs">Play</span>
                        </Button>
                      </Tooltip>
                    ) : (
                      <span className="p-2 text-gray-300 flex-1 text-center">
                        <HiOutlinePlay className="w-4 h-4 inline" />
                      </span>
                    )}
                    {/* Recording Download Button in Actions */}
                    {(log.recordingPublicId || log.recordingUrl) && (
                      <Tooltip position={index === 0 ? 'bottom' : 'top'} text="Download Recording">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="p-2 flex-1"
                          icon={<HiOutlineDownload className="w-4 h-4 text-blue-600" />}
                          onClick={() => handleDownloadRecording(log)}
                        >
                          <span className="text-xs">Save</span>
                        </Button>
                      </Tooltip>
                    )}
                    <Tooltip position={index === 0 ? 'bottom' : 'top'} text="Open WhatsApp Chat">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="p-2 flex-1"
                        icon={<FaWhatsapp className="w-4 h-4 text-green-600" />}
                        onClick={() => handleDirectWhatsApp(log)}
                      >
                        <span className="text-xs">Chat</span>
                      </Button>
                    </Tooltip>
                    <Tooltip position={index === 0 ? 'bottom' : 'top'} text="Send via Template">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="p-2 flex-1"
                        icon={<HiOutlineClipboardList className="w-4 h-4 text-green-700" />}
                        onClick={() => handleOpenWhatsApp(log)}
                      >
                        <span className="text-xs">Template</span>
                      </Button>
                    </Tooltip>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 p-3 sm:p-4 border-t border-gray-100">
                <span className="text-xs sm:text-sm text-gray-500">
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap justify-center">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 1}
                    onClick={() => setPage(p => p - 1)}
                  >
                    <span className="hidden sm:inline">Previous</span>
                    <span className="sm:hidden">Prev</span>
                  </Button>

                  {buildPageList(pagination.page, pagination.totalPages).map(
                    (item, idx) =>
                      item === '...' ? (
                        <span
                          key={`ellipsis-${idx}`}
                          className="px-2 text-sm text-gray-400 select-none"
                        >
                          …
                        </span>
                      ) : (
                        <button
                          key={item}
                          type="button"
                          onClick={() => setPage(item)}
                          className={
                            page === item
                              ? "min-w-[34px] px-2.5 py-1.5 rounded-lg text-sm font-semibold bg-primary-600 text-white transition-colors"
                              : "min-w-[34px] px-2.5 py-1.5 rounded-lg text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                          }
                        >
                          {item}
                        </button>
                      )
                  )}

                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === pagination.totalPages}
                    onClick={() => setPage(p => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </Card>
        )}

        {/* Recording Modal */}
        <Modal
          isOpen={!!selectedLog}
          onClose={() => setSelectedLog(null)}
          title="Call Recording"
          size="md"
        >
          {selectedLog && (
            <div className="space-y-3 sm:space-y-4">
              <div className="bg-gray-50 p-3 sm:p-4 rounded-xl">
                <div className="flex items-start sm:items-center justify-between gap-2 mb-3 sm:mb-4">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-gray-900 truncate">{selectedLog.phoneNumber}</p>
                    <p className="text-xs sm:text-sm text-gray-500 truncate">
                      {selectedLog.leadId?.name || 'Unknown Lead'}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5 sm:mt-1">
                      User: {selectedLog.userId?.firstName} {selectedLog.userId?.lastName}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    <Badge
                      variant={
                        selectedLog.callType === 'incoming' ? 'info' :
                          selectedLog.callType === 'missed' ? 'danger' : 'primary'
                      }
                      size="sm"
                    >
                      {selectedLog.callType || 'outgoing'}
                    </Badge>
                    <Badge variant={selectedLog.status === 'connected' ? 'success' : 'danger'} size="sm">
                      {formatLabel(selectedLog.status)}
                    </Badge>
                  </div>
                </div>
                <div className="text-xs sm:text-sm text-gray-500 mb-3">
                  {selectedLog.createdAt
                    ? format(new Date(selectedLog.createdAt), 'MMM d, yyyy h:mm a')
                    : '-'}
                  {' • '}
                  {formatDuration(selectedLog.duration)}
                </div>
                {audioUrl ? (
                  <TalkTimeAudio
                    className="w-full"
                    src={audioUrl}
                    talkDuration={selectedLog.duration}
                  />
                ) : (
                  <div className="flex items-center justify-center py-4">
                    <Spinner size="sm" />
                    <span className="ml-2 text-gray-500 text-sm">Loading audio...</span>
                  </div>
                )}
              </div>

              {(selectedLog.notes || selectedLog.remarks) && (
                <div className="bg-gray-50 p-3 sm:p-4 rounded-xl space-y-1.5">
                  {selectedLog.notes && (
                    <p className="text-sm text-gray-700">
                      <span className="font-medium text-gray-900">
                        {selectedLog.status === 'connected' ? 'Notes' : 'Reason'}:
                      </span>{' '}
                      {selectedLog.notes}
                    </p>
                  )}
                  {selectedLog.remarks && (
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">
                      <span className="font-medium text-gray-900">Notes:</span> {selectedLog.remarks}
                    </p>
                  )}
                </div>
              )}

              {selectedLog.attachments && selectedLog.attachments.length > 0 && (
                <div className="bg-gray-50 p-3 sm:p-4 rounded-xl">
                  <p className="text-xs sm:text-sm font-medium text-gray-500 mb-2">
                    Attachments ({selectedLog.attachments.length})
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {selectedLog.attachments.map((att, aIndex) => {
                      const isImg = isImageAttachment(att);
                      const url = filePublicUrl(att.url);
                      const label = att.originalName || att.fileName || 'Attachment';
                      const cls = "inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-colors max-w-[240px] cursor-pointer";
                      const inner = (
                        <>
                          <HiOutlinePaperClip className="w-4 h-4 flex-shrink-0" />
                          <span className="text-xs font-medium truncate">{label}</span>
                        </>
                      );
                      return isImg ? (
                        <button
                          key={att._id || `${att.fileName}-${aIndex}`}
                          type="button"
                          title={label}
                          className={cls}
                          onClick={() => setPreviewImage(url)}
                        >
                          {inner}
                        </button>
                      ) : (
                        <a
                          key={att._id || `${att.fileName}-${aIndex}`}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={label}
                          className={cls}
                        >
                          {inner}
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="flex justify-end">
                <Tooltip text="Download recording file">
                  <Button
                    variant="outline"
                    size="sm"
                    icon={<HiOutlineDownload className="w-4 h-4" />}
                    onClick={() => handleDownloadRecording(selectedLog)}
                  >
                    Download
                  </Button>
                </Tooltip>
              </div>
            </div>
          )}
        </Modal>

        {/* WhatsApp Template Modal */}
        <Modal
          isOpen={showWhatsAppModal}
          onClose={() => setShowWhatsAppModal(false)}
          title="Send WhatsApp Message"
          size="md"
        >
          {selectedLogForWhatsApp && (
            <div className="space-y-3 sm:space-y-4">
              {/* Contact Info */}
              <div className="bg-gray-50 p-3 sm:p-4 rounded-xl">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <FaWhatsapp className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 truncate">{selectedLogForWhatsApp.phoneNumber}</p>
                    <p className="text-xs sm:text-sm text-gray-500 truncate">
                      {selectedLogForWhatsApp.leadId?.name || selectedLogForWhatsApp.contactName || 'Unknown Contact'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="grid grid-cols-2 gap-2 sm:gap-3">
                <button
                  onClick={() => handleDirectWhatsApp(selectedLogForWhatsApp)}
                  className="flex flex-col items-center gap-1.5 sm:gap-2 p-3 sm:p-4 rounded-xl border-2 border-green-200 bg-green-50 hover:bg-green-100 transition-colors"
                >
                  <FaWhatsapp className="w-6 h-6 sm:w-8 sm:h-8 text-green-600" />
                  <span className="font-medium text-green-700 text-sm sm:text-base">Direct Chat</span>
                  <span className="text-xs text-green-600 hidden sm:block">Open WhatsApp directly</span>
                </button>
                <div className="flex flex-col items-center gap-1.5 sm:gap-2 p-3 sm:p-4 rounded-xl border-2 border-primary-200 bg-primary-50">
                  <HiOutlineClipboardList className="w-6 h-6 sm:w-8 sm:h-8 text-primary-600" />
                  <span className="font-medium text-primary-700 text-sm sm:text-base">Use Template</span>
                  <span className="text-xs text-primary-600 hidden sm:block">Select a message template</span>
                </div>
              </div>

              {/* Template Selection */}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                  Select Template
                </label>
                {templates.length === 0 ? (
                  <p className="text-xs sm:text-sm text-gray-500">No templates available</p>
                ) : (
                  <div className="space-y-2 max-h-48 sm:max-h-60 overflow-y-auto">
                    {templates.map((template) => (
                      <div
                        key={template._id}
                        onClick={() => setSelectedTemplate(template)}
                        className={`p-2.5 sm:p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          selectedTemplate?._id === template._id
                            ? 'border-green-500 bg-green-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-gray-900 text-sm">{template.name}</span>
                          <Badge variant="default" size="sm">{template.category}</Badge>
                        </div>
                        <p className="text-xs sm:text-sm text-gray-600 line-clamp-2">{template.message}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Preview */}
              {selectedTemplate && (
                <div className="bg-green-50 p-3 sm:p-4 rounded-xl border border-green-200">
                  <div className="flex items-center gap-1.5 sm:gap-2 mb-1.5 sm:mb-2">
                    <FaWhatsapp className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-600" />
                    <span className="text-xs sm:text-sm font-medium text-green-700">Message Preview</span>
                  </div>
                  <p className="text-xs sm:text-sm text-gray-700 whitespace-pre-wrap">
                    {selectedTemplate.message
                      .replace(/\{\{name\}\}/gi, selectedLogForWhatsApp.leadId?.name || selectedLogForWhatsApp.contactName || 'Customer')
                      .replace(/\{\{email\}\}/gi, selectedLogForWhatsApp.leadId?.email || '')
                      .replace(/\{\{phone\}\}/gi, selectedLogForWhatsApp.phoneNumber || '')}
                  </p>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-2 sm:gap-3">
                <Button variant="ghost" size="sm" onClick={() => setShowWhatsAppModal(false)}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  icon={<FaWhatsapp className="w-4 h-4" />}
                  onClick={handleSendWhatsApp}
                  disabled={!selectedTemplate}
                >
                  Send
                </Button>
              </div>
            </div>
          )}
        </Modal>
      </div>

      {/* Attachment image preview lightbox */}
      <ImagePreviewModal src={previewImage} onClose={() => setPreviewImage(null)} />
    </Layout>
  );
};

export default CallLogs;